import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useRoute, useLocation } from "wouter";
import { ChevronRight, Save, MoreVertical, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import SalviaLayout from "@/components/SalviaLayout";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { maskCNPJ } from "@/lib/masks";
import CompanyStatusBadges from "@/components/ClientDetail/CompanyStatusBadges";
import OverviewTab from "@/components/ClientDetail/OverviewTab";
import StructureTab from "@/components/ClientDetail/StructureTab";
import GrupoEconomicoTab from "@/components/ClientDetail/GrupoEconomicoTab";
import ClientUsersTab from "@/components/ClientDetail/ClientUsersTab";
import ClientContractsTab from "@/components/ClientDetail/ClientContractsTab";
import ClientInitialContractTab from "@/components/ClientDetail/ClientInitialContractTab";
import ClientIntegrationsTab from "@/components/ClientDetail/ClientIntegrationsTab";
import { toast } from "sonner";
import { usePageTitle } from "@/hooks/usePageTitle";
import { canManageLifeBaseGovernance } from "@shared/lifeBasePermissions";
import {
  EMPTY_INITIAL_CLIENT_CONTRACT,
  InitialClientContractDraft,
  isInitialClientContractComplete,
  validateInitialClientContract,
} from "@/lib/clientContractDraft";

// ─── Helper: parseia erros Zod do tRPC e evita exibição de JSON bruto ───
function parseZodErrorFromTRPC(error: any): { fieldErrors: Record<string, string>; formError: string } | null {
  const zodError = error?.data?.zodError;
  if (zodError) {
    const fieldErrors: Record<string, string> = {};
    if (zodError.fieldErrors && typeof zodError.fieldErrors === "object") {
      Object.entries(zodError.fieldErrors).forEach(([field, msgs]) => {
        fieldErrors[field] = Array.isArray(msgs) ? (msgs[0] as string) : String(msgs);
      });
    }
    const formError =
      (Array.isArray(zodError.formErrors) && zodError.formErrors.length > 0
        ? zodError.formErrors[0]
        : null) ||
      (Object.keys(fieldErrors).length > 0
        ? "Revise os campos destacados para continuar."
        : null);
    if (Object.keys(fieldErrors).length > 0 || formError) {
      return { fieldErrors, formError: formError || "Revise os campos destacados para continuar." };
    }
  }
  // Fallback: parsear JSON bruto no message (legado)
  const msg = error?.message || "";
  if (msg.startsWith("[") || msg.startsWith("{")) {
    try {
      const parsed = JSON.parse(msg);
      if (Array.isArray(parsed)) {
        const fieldErrors: Record<string, string> = {};
        parsed.forEach((issue: any) => {
          const field = Array.isArray(issue.path) && issue.path.length > 0
            ? issue.path.join(".")
            : "form";
          if (!fieldErrors[field]) fieldErrors[field] = issue.message || "Erro de validação";
        });
        return { fieldErrors, formError: "Revise os campos destacados para continuar." };
      }
    } catch {
      // não é JSON válido
    }
  }
  return null;
}

const OVERVIEW_FIELD_LABELS: Record<string, string> = {
  channelId: "Canal",
  nomeFantasia: "Nome Fantasia",
  razaoSocial: "Razão Social",
  cnpj: "CNPJ",
  cnae: "CNAE",
  atividadePrincipal: "Atividade Principal",
  grauRisco: "Grau de Risco",
  sector: "Setor",
  employeesCount: "Número de Funcionários",
  logradouro: "Logradouro",
  numero: "Número",
  bairro: "Bairro",
  cidade: "Cidade",
  estado: "Estado",
  cep: "CEP",
  email: "E-mail",
  phone: "Telefone",
  responsibleName: "Responsável",
};

const CONTRACT_FIELD_LABELS: Record<string, string> = {
  contractName: "Nome do contrato",
  contractNumber: "Número do contrato",
  status: "Status",
  signedAt: "Assinado em",
  startsAt: "Início da vigência",
  endsAt: "Fim da vigência",
  notes: "Observações",
  serviceIds: "Serviços incluídos no contrato",
};

function buildValidationSummary(
  fieldErrors: Record<string, string>,
  labels: Record<string, string>,
  fallbackMessage: string,
) {
  const fields = Object.keys(fieldErrors)
    .map((field) => labels[field] || field)
    .filter(Boolean);

  if (fields.length === 0) {
    return fallbackMessage;
  }

  const uniqueFields = Array.from(new Set(fields));
  return `Preencha ou corrija os campos obrigatórios: ${uniqueFields.join(", ")}.`;
}

function normalizeOptionalContactFields<T extends Record<string, any> | null | undefined>(value: T): T {
  if (!value || typeof value !== "object") {
    return value;
  }

  return {
    ...value,
    email: typeof value.email === "string" ? value.email.trim() : "",
    phone: typeof value.phone === "string" ? value.phone.trim() : "",
    responsibleName: typeof value.responsibleName === "string" ? value.responsibleName.trim() : "",
  } as T;
}

function splitClientSaveErrors(fieldErrors: Record<string, string>) {
  const overviewErrors: Record<string, string> = {};
  const contractErrors: Record<string, string> = {};
  const contractFields = new Set([
    "contractName",
    "contractNumber",
    "status",
    "startsAt",
    "endsAt",
    "signedAt",
    "notes",
    "serviceIds",
  ]);

  Object.entries(fieldErrors).forEach(([field, message]) => {
    const normalizedField = field.startsWith("initialContract.")
      ? field.replace(/^initialContract\./, "")
      : field;

    if (contractFields.has(normalizedField)) {
      contractErrors[normalizedField] = message;
      return;
    }

    overviewErrors[field] = message;
  });

  return { overviewErrors, contractErrors };
}

interface ClientDetailPageProps {
  params?: { id?: string };
}

export default function ClientDetailPage() {
  usePageTitle("Detalhes do Cliente");
  const [matchId, paramsId] = useRoute("/clients/:id");
  const [matchNew] = useRoute("/clients/new");
  const [location, navigate] = useLocation();
  const { user } = useAuth();
  const utils = trpc.useUtils();
  
  // Determinar se é novo ou edição
  const isNew = matchNew || (paramsId?.id === 'new');
  const clientId = !isNew && paramsId?.id ? parseInt(paramsId.id) : null;
  const validClientId = clientId && !isNaN(clientId) ? clientId : null;
  
  const isGlobalPlatformAdmin = ["admin", "salvia_operation_admin"].includes(user?.role || "");
  // Read-only mode para toda a camada estrutural de Canal e também para RH cliente
  const isReadOnly = ['channel_admin', 'channel_admin_multi', 'channel_operator', 'channel_consultant', 'client_rh'].includes(user?.role || '');
  const isContractsReadOnly = !isGlobalPlatformAdmin;
  const isIntegrationsReadOnly = isReadOnly || !canManageLifeBaseGovernance(user?.role);
  
  // Extrair query params para contexto de grupo
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const parentId = searchParams.get('parentId');
  const parentTab = searchParams.get('parentTab') ?? 'grupo';
  
  // Validar parentId (deve ser número inteiro positivo)
  const isValidParentId = parentId && /^\d+$/.test(parentId) && parseInt(parentId) > 0;
  const validatedParentId = isValidParentId ? parseInt(parentId!) : null;

  // fromMatriz: quando a filial é aberta a partir da aba Matriz e Filiais de uma matriz
  const fromMatrizParam = searchParams.get('fromMatriz');
  const isValidFromMatriz = fromMatrizParam && /^\d+$/.test(fromMatrizParam) && parseInt(fromMatrizParam) > 0;
  const fromMatrizId = isValidFromMatriz ? parseInt(fromMatrizParam!) : null;

  // Aba inicial: respeitar ?tab= se informado (ex: tab=structure ao voltar da filial)
  const tabParam = searchParams.get('tab');
  const validTabParam = tabParam && ['overview', 'structure', 'group', 'users', 'contracts', 'integrations'].includes(tabParam) ? tabParam : 'overview';

  const [activeTab, setActiveTab] = useState(validTabParam);
  const [company, setCompany] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [contractFieldErrors, setContractFieldErrors] = useState<Record<string, string>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [initialContract, setInitialContract] = useState<InitialClientContractDraft>(EMPTY_INITIAL_CLIENT_CONTRACT);
  const [contractTabSaveHandler, setContractTabSaveHandler] = useState<null | (() => Promise<boolean>)>(null);
  const handleRegisterContractTabSaveHandler = useCallback((handler: null | (() => Promise<boolean>)) => {
    setContractTabSaveHandler(() => handler);
  }, []);

  // Reset aba ativa quando navega entre clientes (mesma rota /clients/:id, componente não desmonta)
  useEffect(() => {
    setActiveTab(validTabParam);
    setCompany(null);
    setFormData(null);
    setIsLoading(!isNew);
    setFieldErrors({});
    setContractFieldErrors({});
    setHasAttemptedSubmit(false);
    setInitialContract(EMPTY_INITIAL_CLIENT_CONTRACT);
  }, [validClientId]);

  // Buscar cliente se estiver editando
  const { data: clientData, isLoading: isLoadingClient } = trpc.clients.getById.useQuery(
    { id: validClientId! },
    { enabled: !!validClientId && !isNew }
  );

  // Buscar canais disponíveis para perfis administrativos que podem escolher o canal na criação do cliente
  const { data: channelsData } = trpc.channels.list.useQuery(undefined, {
    enabled: ["admin", "salvia_operation_admin", "channel_admin", "channel_admin_multi"].includes(user?.role || ""),
  });
  
  // Buscar controladora usando validatedParentId
  const { data: parentCompanyData, isLoading: isLoadingParent } = trpc.clients.getById.useQuery(
    { id: validatedParentId! },
    { enabled: !!validatedParentId }
  );
  
  // Nome da controladora: usar dados da query ou fallback
  const parentCompanyName = parentCompanyData?.nomeFantasia || parentCompanyData?.name || parentCompanyData?.razaoSocial || "Controladora";

  // Buscar dados da matriz de origem quando vindo da aba Matriz e Filiais
  const { data: fromMatrizData } = trpc.clients.getById.useQuery(
    { id: fromMatrizId! },
    { enabled: !!fromMatrizId }
  );
  const fromMatrizName = (fromMatrizData as any)?.nomeFantasia || (fromMatrizData as any)?.name || (fromMatrizData as any)?.razaoSocial || "Matriz";
  


  // Mutations
  const createClientMutation = trpc.clients.create.useMutation();
  const updateClientMutation = trpc.clients.update.useMutation();

  // Carregar dados do cliente
  useEffect(() => {
    if (clientData) {
      setCompany(clientData);
      setFormData(normalizeOptionalContactFields(clientData));
      setIsLoading(false);

    }
  }, [clientData]);

  // Inicializar formulário vazio para novo cliente
  useEffect(() => {
    if (isNew && !formData) {
      setFormData({
        channelId: user?.channelId || 0,
        nomeFantasia: "",
        razaoSocial: "",
        cnpj: "",
        logradouro: "",
        numero: "",
        complemento: "",
        bairro: "",
        cidade: "",
        estado: "",
        cep: "",
        cnae: "",
        atividadePrincipal: "",
        grauRisco: "",
        sector: "",
        employeesCount: 0,
        email: "",
        phone: "",
        responsibleName: "",
        isMatriz: false,
        isControladora: false,
      });
      setIsLoading(false);
      setInitialContract(EMPTY_INITIAL_CLIENT_CONTRACT);
    }
  }, [isNew, user, formData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
    // Limpar erro do campo
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleInitialContractChange = useCallback((nextValue: InitialClientContractDraft) => {
    setInitialContract(nextValue);
    setContractFieldErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const nextErrors = { ...prev };
      if (nextValue.contractName.trim()) {
        delete nextErrors.contractName;
      }
      if (nextValue.contractNumber.trim()) {
        delete nextErrors.contractNumber;
      }
      if (nextValue.status) {
        delete nextErrors.status;
      }
      if (nextValue.signedAt) {
        delete nextErrors.signedAt;
      }
      if (nextValue.startsAt) {
        delete nextErrors.startsAt;
      }
      if (nextValue.endsAt) {
        delete nextErrors.endsAt;
      }
      if (nextValue.serviceIds.length > 0) {
        delete nextErrors.serviceIds;
      }
      return nextErrors;
    });
  }, []);

  // ─── V33: Validação frontend com mensagens inline e scroll automático ───
  const REQUIRED_FIELDS: { field: string; label: string; minLen?: number }[] = [
    { field: "nomeFantasia", label: "Nome Fantasia", minLen: 2 },
    { field: "razaoSocial", label: "Razão Social", minLen: 2 },
    { field: "cnpj", label: "CNPJ" },
    { field: "cnae", label: "CNAE", minLen: 7 },
    { field: "atividadePrincipal", label: "Atividade Principal", minLen: 2 },
    { field: "grauRisco", label: "Grau de Risco" },
    { field: "sector", label: "Setor", minLen: 2 },
    { field: "employeesCount", label: "Número de Funcionários" },
    { field: "logradouro", label: "Logradouro", minLen: 2 },
    { field: "numero", label: "Número" },
    { field: "bairro", label: "Bairro", minLen: 2 },
    { field: "cidade", label: "Cidade", minLen: 2 },
    { field: "estado", label: "Estado" },
    { field: "cep", label: "CEP" },
  ];

  const validateForm = useCallback((): Record<string, string> => {
    if (!formData) return {};
    const errors: Record<string, string> = {};

    for (const { field, label, minLen } of REQUIRED_FIELDS) {
      const val = formData[field];

      if (field === "employeesCount") {
        const numericValue = Number(val);
        if (!Number.isFinite(numericValue) || numericValue <= 0) {
          errors[field] = `${label} é obrigatório e deve ser maior que zero.`;
        }
        continue;
      }

      if (!val || String(val).trim() === "") {
        errors[field] = `${label} é obrigatório.`;
      } else if (minLen && String(val).trim().length < minLen) {
        errors[field] = `${label} deve ter pelo menos ${minLen} caracteres.`;
      }
    }

    // Validação de formato: CNPJ (14 dígitos)
    if (formData.cnpj && !errors.cnpj) {
      const cnpjDigits = formData.cnpj.replace(/\D/g, "");
      if (cnpjDigits.length !== 14) {
        errors.cnpj = "CNPJ inválido: informe 14 dígitos.";
      }
    }

    // Validação de formato: CEP (8 dígitos)
    if (formData.cep && !errors.cep) {
      const cepDigits = formData.cep.replace(/\D/g, "");
      if (cepDigits.length !== 8) {
        errors.cep = "CEP deve conter 8 dígitos.";
      }
    }

    // Validação de formato: Estado (2 letras maiúsculas)
    if (formData.estado && !errors.estado) {
      if (!/^[A-Z]{2}$/.test(formData.estado)) {
        errors.estado = "Estado deve ser a sigla com 2 letras (ex: SP).";
      }
    }

    // Validação de formato: Email (se preenchido)
    if (formData.email && formData.email.trim() !== "") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        errors.email = "E-mail inválido. Use o formato: nome@empresa.com.";
      }
    }

    // Canal obrigatório para perfis administrativos globais
    if (isGlobalPlatformAdmin && (!formData.channelId || formData.channelId === 0)) {
      errors.channelId = "Canal é obrigatório. Selecione um canal válido.";
    }

    return errors;
  }, [formData, isGlobalPlatformAdmin]);

  // Scroll automático para o primeiro campo com erro
  const scrollToFirstError = useCallback((errors: Record<string, string>) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField) return;
    // Tentar encontrar o elemento pelo id ou name
    const el = document.getElementById(firstErrorField) ||
               document.querySelector(`[name="${firstErrorField}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Tentar focar o campo
      if (typeof (el as HTMLElement).focus === "function") {
        setTimeout(() => (el as HTMLElement).focus(), 400);
      }
    }
  }, []);

  const liveOverviewErrors = useMemo(() => validateForm(), [validateForm]);
  const liveContractErrors = useMemo(
    () => (isNew ? validateInitialClientContract(initialContract) : {}),
    [initialContract, isNew],
  );

  const displayOverviewErrors = useMemo(() => {
    const nextErrors = { ...fieldErrors };

    Object.entries(liveOverviewErrors).forEach(([field, message]) => {
      const rawValue = formData?.[field];
      const hasMeaningfulValue = field === "employeesCount"
        ? Number(rawValue) > 0 || rawValue === 0
        : typeof rawValue === "string"
          ? rawValue.trim().length > 0
          : Boolean(rawValue);

      if (hasAttemptedSubmit || hasMeaningfulValue) {
        nextErrors[field] = message;
      }
    });

    return nextErrors;
  }, [fieldErrors, formData, hasAttemptedSubmit, liveOverviewErrors]);

  const displayContractErrors = useMemo(() => {
    const nextErrors = { ...contractFieldErrors };

    Object.entries(liveContractErrors).forEach(([field, message]) => {
      const rawValue = (initialContract as Record<string, unknown>)[field];
      const hasMeaningfulValue = Array.isArray(rawValue)
        ? rawValue.length > 0
        : typeof rawValue === "string"
          ? rawValue.trim().length > 0
          : Boolean(rawValue);

      if (hasAttemptedSubmit || hasMeaningfulValue) {
        nextErrors[field] = message;
      }
    });

    return nextErrors;
  }, [contractFieldErrors, hasAttemptedSubmit, initialContract, liveContractErrors]);

  const overviewValidationSummary = useMemo(
    () => buildValidationSummary(
      liveOverviewErrors,
      OVERVIEW_FIELD_LABELS,
      "Preencha os campos obrigatórios da Visão Geral para habilitar o salvamento.",
    ),
    [liveOverviewErrors],
  );

  const contractValidationSummary = useMemo(
    () => buildValidationSummary(
      liveContractErrors,
      CONTRACT_FIELD_LABELS,
      "Preencha os campos obrigatórios do contrato inicial para habilitar o salvamento.",
    ),
    [liveContractErrors],
  );

  const isNewClientReadyToSave = !isNew || (
    Object.keys(liveOverviewErrors).length === 0 &&
    Object.keys(liveContractErrors).length === 0 &&
    isInitialClientContractComplete(initialContract)
  );

  const handleSave = async (continueToNextTab?: boolean) => {
    if (!isNew && activeTab === "contracts" && company?.id) {
      if (!contractTabSaveHandler) {
        toast.info("Abra um contrato existente ou clique em 'Novo contrato' para editar antes de usar o salvar global.");
        return;
      }

      const didSaveContract = await contractTabSaveHandler();
      if (didSaveContract && continueToNextTab) {
        setActiveTab("integrations");
      }
      return;
    }

    // V33: Validação frontend antes de enviar ao backend
    setHasAttemptedSubmit(true);
    const frontendErrors = validateForm();
    const initialContractErrors = isNew ? validateInitialClientContract(initialContract) : {};

    if (continueToNextTab && isNew && activeTab === "overview") {
      if (Object.keys(frontendErrors).length > 0) {
        setFieldErrors(frontendErrors);
        toast.error(
          buildValidationSummary(
            frontendErrors,
            OVERVIEW_FIELD_LABELS,
            "Preencha os campos obrigatórios da Visão Geral para continuar.",
          ),
          { duration: 5000 }
        );
        scrollToFirstError(frontendErrors);
        return;
      }

      setFieldErrors({});
      setActiveTab("contracts");
      toast.info("Agora preencha o contrato inicial para concluir o cadastro do cliente.");
      return;
    }

    if (Object.keys(frontendErrors).length > 0) {
      setFieldErrors(frontendErrors);
      toast.error(
        buildValidationSummary(
          frontendErrors,
          OVERVIEW_FIELD_LABELS,
          "Preencha os campos obrigatórios da Visão Geral para continuar.",
        ),
        { duration: 5000 }
      );
      setActiveTab("overview");
      scrollToFirstError(frontendErrors);
      return;
    }

    if (Object.keys(initialContractErrors).length > 0) {
      setFieldErrors({});
      setContractFieldErrors(initialContractErrors);
      setActiveTab("contracts");
      toast.error(
        buildValidationSummary(
          initialContractErrors,
          CONTRACT_FIELD_LABELS,
          "Preencha os campos obrigatórios do contrato inicial para concluir o cadastro.",
        ),
      );
      return;
    }

    setFieldErrors({});
    setContractFieldErrors({});
    setIsSaving(true);
    try {
      if (isNew) {
        // V84: Sanitizar complemento — enviar null quando vazio para persistir limpeza de forma explícita
        const createPayload = normalizeOptionalContactFields({ ...formData });
        if (createPayload.complemento === "" || createPayload.complemento === null) {
          createPayload.complemento = null;
        }
        const result = await createClientMutation.mutateAsync({
          ...createPayload,
          initialContract: {
            contractName: initialContract.contractName.trim(),
            contractNumber: initialContract.contractNumber.trim() || null,
            status: initialContract.status,
            startsAt: initialContract.startsAt || null,
            endsAt: initialContract.endsAt || null,
            signedAt: initialContract.signedAt || null,
            notes: initialContract.notes.trim() || null,
            serviceIds: initialContract.serviceIds,
          },
        });
        const createdCompany = (result as any)?.data || result;

        if (!createdCompany?.id) {
          throw new Error("Não foi possível identificar o cliente recém-criado após a conclusão do cadastro.");
        }

        toast.success("Cliente e contrato inicial salvos com sucesso!");
        setCompany(createdCompany);
        setFormData(normalizeOptionalContactFields(createdCompany));
        utils.clients.listRepresentatives.invalidate();
        await utils.platformContracts.summary.invalidate({ companyId: createdCompany.id });
        await utils.platformContracts.listContracts.invalidate({ companyId: createdCompany.id });

        const nextTab = continueToNextTab
          ? createdCompany.isMatriz
            ? "structure"
            : createdCompany.isControladora
              ? "group"
              : "users"
          : "contracts";

        navigate(`/clients/${createdCompany.id}?tab=${nextTab}`);
      } else {
        // Defesa: se for filial, não enviar channelId
        const payloadToSend = normalizeOptionalContactFields({ ...formData });
        if (isFilial) {
          delete payloadToSend.channelId;
        }
        // V84: Sanitizar complemento — enviar null se vazio para garantir que o campo seja realmente limpo
        if (payloadToSend.complemento === "" || payloadToSend.complemento === null) {
          payloadToSend.complemento = null;
        }
        
        const result = await updateClientMutation.mutateAsync({
          id: validClientId!,
          ...payloadToSend,
        });
        // O router retorna { ok, data, message } — extrair o objeto do cliente
        const updatedCompany = (result as any)?.data || result;
        toast.success("Cliente atualizado com sucesso!");
        setCompany(updatedCompany);
        setFormData(normalizeOptionalContactFields(updatedCompany));
        // Invalidar a query para garantir que os dados sejam recarregados
        await utils.clients.getById.invalidate({ id: validClientId! });
        // V15: Invalidar cache de representantes para refletir alteração de isRepresentative imediatamente
        utils.clients.listRepresentatives.invalidate();
        
        if (continueToNextTab && updatedCompany.isMatriz) {
          setActiveTab("structure");
        } else if (continueToNextTab && updatedCompany.isControladora) {
          setActiveTab("group");
        }
      }
    } catch (error: any) {
      // Erro ao salvar cliente — nunca exibir JSON bruto
      const parsed = parseZodErrorFromTRPC(error);
      if (parsed) {
        const { overviewErrors, contractErrors } = splitClientSaveErrors(parsed.fieldErrors);
        setFieldErrors(overviewErrors);
        setContractFieldErrors(contractErrors);

        if (Object.keys(contractErrors).length > 0) {
          setActiveTab("contracts");
        } else if (Object.keys(overviewErrors).length > 0) {
          setActiveTab("overview");
          scrollToFirstError(overviewErrors);
        }

        const hasOverviewErrors = Object.keys(overviewErrors).length > 0;
        const hasContractErrors = Object.keys(contractErrors).length > 0;

        const detailedMessage = hasOverviewErrors
          ? buildValidationSummary(
              overviewErrors,
              OVERVIEW_FIELD_LABELS,
              parsed.formError,
            )
          : hasContractErrors
            ? buildValidationSummary(
                contractErrors,
                CONTRACT_FIELD_LABELS,
                parsed.formError,
              )
            : parsed.formError;

        toast.error(detailedMessage, { duration: 5000 });
      } else {
        const msg = error?.message || "";
        const isRawJson = msg.startsWith("[") || msg.startsWith("{");
        toast.error(isRawJson ? "Não foi possível salvar o cliente. Verifique os dados e tente novamente." : (msg || "Erro ao salvar cliente"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || (isLoadingClient && !!validClientId)) {
    return (
      <SalviaLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </SalviaLayout>
    );
  }

  const displayName = formData?.nomeFantasia || formData?.name || "Novo Cliente";
  const displayCNPJ = formData?.cnpj ? maskCNPJ(formData.cnpj) : "---";
  const displayStatus = company?.status === "active" ? "ATIVA" : "INATIVA";
  const isFilial = company?.type === 'filial' || !!company?.matrizId;
  const matrizId = company?.matrizId;
  // Usar validClientId em vez de clientId para evitar NaN
  const effectiveClientId = validClientId;

  return (
    <SalviaLayout>
      <div className="flex flex-col h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b px-6 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <button
              onClick={() => navigate('/clients')}
              className="hover:text-gray-900 hover:underline cursor-pointer"
            >
              Clientes
            </button>
            <ChevronRight className="w-4 h-4" />
            <span>Empresas</span>
            
            {/* Breadcrumb contextual: Controladora (quando vindo do Grupo Econômico) */}
            {validatedParentId && (
              <>
                <ChevronRight className="w-4 h-4" />
                <button
                  onClick={() => navigate(`/clients/${validatedParentId}?tab=${parentTab}`)}
                  className="hover:text-gray-900 hover:underline cursor-pointer"
                >
                  {parentCompanyName}
                </button>
              </>
            )}
            
            {/* Breadcrumb: Matriz de origem (quando vindo da aba Matriz e Filiais via fromMatriz) */}
            {fromMatrizId && (
              <>
                <ChevronRight className="w-4 h-4" />
                <button
                  onClick={() => navigate(`/clients/${fromMatrizId}?tab=structure`)}
                  className="hover:text-gray-900 hover:underline cursor-pointer"
                >
                  {fromMatrizName}
                </button>
              </>
            )}

            {/* Breadcrumb padrão: Matriz (quando é filial e não vindo do Grupo nem de fromMatriz) */}
            {!validatedParentId && !fromMatrizId && isFilial && matrizId && (
              <>
                <ChevronRight className="w-4 h-4" />
                <button
                  onClick={() => navigate(`/clients/${matrizId}?tab=structure`)}
                  className="hover:text-gray-900 hover:underline cursor-pointer"
                >
                  {(company as any)?.matrizName || "Matriz"}
                </button>
              </>
            )}
            
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">{displayName}</span>
          </div>
        </div>

        {/* Header */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
              <p className="text-sm text-gray-600 mt-1">
                CNPJ {displayCNPJ} • Status: {displayStatus}
              </p>
            </div>
            {!isReadOnly && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleSave(false)}
                  disabled={isSaving || (isNew && !isNewClientReadyToSave) || (!isNew && activeTab === "contracts" && !contractTabSaveHandler)}
                  className="gap-2"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Salvar
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSave(true)}
                  disabled={isSaving || (isNew && !isNewClientReadyToSave) || (!isNew && activeTab === "contracts" && !contractTabSaveHandler)}
                >
                  Salvar e continuar
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>
                      {company?.status === "active" ? "Desativar" : "Ativar"}
                    </DropdownMenuItem>
                    <DropdownMenuItem>Exportar</DropdownMenuItem>
                    <DropdownMenuItem>Ver auditoria</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          {/* Chips de Status */}
          {company && <CompanyStatusBadges company={company} />}

          {isNew && !isReadOnly && !isNewClientReadyToSave && (
            <Alert className="mt-3 border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="space-y-2 text-amber-900">
                <p>
                  O botão <strong>Salvar</strong> será habilitado somente quando todos os campos obrigatórios da <strong>Visão Geral</strong> e do <strong>Contrato inicial</strong> estiverem preenchidos corretamente.
                </p>
                {Object.keys(liveOverviewErrors).length > 0 && (
                  <p>
                    <strong>Visão Geral:</strong> {overviewValidationSummary}
                  </p>
                )}
                {Object.keys(liveContractErrors).length > 0 && (
                  <p>
                    <strong>Contrato inicial:</strong> {contractValidationSummary}
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}
          
          {/* Banner read-only */}
          {isReadOnly && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-800">Visualização somente leitura — neste contexto você pode consultar os dados cadastrais e navegar pelas abas, sem editar este cliente.</span>
            </div>
          )}
        </div>

        {/* Tabs e Conteúdo */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6 grid h-auto w-full grid-cols-2 gap-2 bg-transparent p-0 md:grid-cols-3 xl:grid-cols-6">
              <TabsTrigger value="overview">Visão Geral</TabsTrigger>
              <TabsTrigger value="contracts">Contratos</TabsTrigger>
              <TabsTrigger value="integrations" disabled={!company?.id}>
                Integrações
              </TabsTrigger>
              <TabsTrigger value="structure" disabled={!company?.id}>
                Matriz e Filiais
              </TabsTrigger>
              <TabsTrigger value="group" disabled={!company?.id}>
                Grupo Econômico
              </TabsTrigger>
              <TabsTrigger value="users" disabled={!company?.id}>
                Usuários
              </TabsTrigger>
            </TabsList>

            {/* Tab Visão Geral */}
            <TabsContent value="overview" className="space-y-6">
              {!company?.id && (
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Preencha a Visão Geral e avance para a aba Contratos, que agora faz parte obrigatória do cadastro inicial. As demais abas serão liberadas após a criação do cliente.
                  </AlertDescription>
                </Alert>
              )}
              <OverviewTab
                formData={formData}
                fieldErrors={displayOverviewErrors}
                onInputChange={handleInputChange}
                channels={channelsData}
                user={user}
                isBranch={isFilial}
                companyId={validClientId}
                readOnly={isReadOnly}
              />
            </TabsContent>

            {/* Tab Integrações */}
            <TabsContent value="integrations" className="space-y-6">
              {!company?.id ? (
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Salve o cliente primeiro para configurar integrações.
                  </AlertDescription>
                </Alert>
              ) : (
                <ClientIntegrationsTab
                  companyId={company.id}
                  companyName={company.nomeFantasia ?? company.razaoSocial}
                  channelId={company.channelId ?? formData?.channelId}
                  readOnly={isIntegrationsReadOnly}
                />
              )}
            </TabsContent>

            {/* Tab Matriz e Filiais */}
            <TabsContent value="structure" className="space-y-6">
              {!company?.id ? (
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Salve o cliente primeiro para acessar esta aba
                  </AlertDescription>
                </Alert>
              ) : (
                <StructureTab
                  company={company}
                  isLoading={false}
                  onCreateFilial={() => {}}
                  onRefresh={() => {}}
                />
              )}
            </TabsContent>

            {/* Tab Grupo Econômico */}
            <TabsContent value="group" className="space-y-6">
              {!company?.id ? (
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Salve o cliente primeiro para acessar esta aba
                  </AlertDescription>
                </Alert>
              ) : (
                <GrupoEconomicoTab
                  companyId={company.id}
                  isControladora={company.isControladora}
                  controladoraId={company.controladoraId}
                />
              )}
            </TabsContent>

            {/* Tab Usuários do Cliente */}
            <TabsContent value="users" className="space-y-6">
              {!company?.id ? (
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Salve o cliente primeiro para gerenciar os usuários vinculados.
                  </AlertDescription>
                </Alert>
              ) : (
                <ClientUsersTab
                  companyId={company.id}
                  channelId={company.channelId ?? formData?.channelId}
                  readOnly={isReadOnly}
                />
              )}
            </TabsContent>

            {/* Tab Contratos do Cliente */}
            <TabsContent value="contracts" className="space-y-6">
              {!company?.id ? (
                <ClientInitialContractTab
                  channelId={formData?.channelId}
                  value={initialContract}
                  onChange={handleInitialContractChange}
                  fieldErrors={displayContractErrors}
                  readOnly={isReadOnly}
                />
              ) : (
                <ClientContractsTab
                  companyId={company.id}
                  channelId={company.channelId ?? formData?.channelId}
                  readOnly={isContractsReadOnly}
                  onRegisterSaveHandler={handleRegisterContractTabSaveHandler}
                />
              )}
            </TabsContent>

          </Tabs>
        </div>
      </div>
    </SalviaLayout>
  );
}
